import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Building2, Wrench, ClipboardCheck } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const FacilitiesMaintenance = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/facilities-maintenance"]?.title} description={seoData["industries/facilities-maintenance"]?.description} path="/industries/facilities-maintenance" keywords={seoData["industries/facilities-maintenance"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Facilities Maintenance"
        description="Full service industrial coating and facilities maintenance contractor. Plant maintenance coatings, equipment repair, floor systems, and preventive maintenance programs for commercial and industrial facilities across California."
        backgroundKey="facilities-maintenance"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Ongoing corrosion and deterioration of building systems and equipment',
              'Floor coating wear in high-traffic and heavy-use areas',
              'Roof leaks and membrane failures on flat commercial roofs',
              'Pipe corrosion in mechanical and utility systems',
              'Concrete spalling in parking structures and loading areas',
              'Deferred maintenance creating escalating repair costs'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-indigo-50 rounded-lg">
                <Building2 className="text-indigo-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Preventive Coating Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Scheduled coating inspection and maintenance programs to protect assets before failure. Systematic surface assessment, spot repair, and recoating to extend infrastructure life and reduce total cost of ownership.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Industrial Floor Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">High-performance floor coating systems for warehouses, maintenance shops, mechanical rooms, and parking structures. Heavy-duty epoxy, polyurethane, and MMA systems for long-lasting performance.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Roof Coating & Waterproofing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Roof leak repair, membrane encapsulation, and reflective roof coating systems for flat commercial and industrial roofs. Extend roof life and improve energy efficiency.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-indigo-600 mr-3 flex-shrink-0" size={22} />
                  Mechanical System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pipe repair, pump rebuild, and equipment restoration for HVAC, plumbing, and process mechanical systems. Cold-applied repair solutions without hot work.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Facilities Maintenance?</h2>
              <div className="space-y-3">
                {['Full service contractor — single point of contact for all coating and repair needs',
                  'Preventive maintenance programs to reduce total cost of ownership',
                  'Flexible scheduling — off-hours, weekends, and phased work available',
                  'Turnkey service — assessment, surface prep, application, and QA/QC',
                  '25+ years facilities maintenance experience',
                  'Emergency response for urgent repair needs'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-indigo-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Turnkey Facilities Partner</h3>
              <p className="text-indigo-100 mb-6">BCS serves as a single-source contractor for all your facility coating and repair needs. Preventive maintenance programs that protect your assets and reduce long-term costs.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/maintenance/smart-program')}>
                View Maintenance Program <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Simplify Your Facilities Maintenance</h2>
          <p className="text-blue-100 mb-8">Contact us for a comprehensive facility assessment and customized maintenance program.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FacilitiesMaintenance;
